/**
 * User-facing text for a 429 from a sign-in step (password or MFA code).
 *
 * Both the per-IP throttle and the per-account lockout (CR-8657) answer 429
 * with the API's `{status, code, message}` envelope, where `message` ends in
 * "... N seconds." This turns that into a wait the user can act on, so a
 * person whose password is correct is never told it is wrong.
 *
 * @example
 * signInRateLimitMessage('Too many failed sign-in attempts. Try again in 900 seconds.')
 * // => 'Too many sign-in attempts. Try again in about 15 minutes.'
 *
 * @param serverMessage - The `message` field of the 429 response, if any
 * @returns A sentence starting "Too many sign-in attempts."
 */
export function signInRateLimitMessage(serverMessage: unknown): string {
  const seconds = Number(String(serverMessage ?? '').match(/(\d+)\s*seconds?/)?.[1]);
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return 'Too many sign-in attempts. Please wait a few minutes and try again.';
  }
  const minutes = Math.max(1, Math.ceil(seconds / 60));
  return `Too many sign-in attempts. Try again in about ${minutes} minute${minutes === 1 ? '' : 's'}.`;
}

/** Shape of an axios error from the MFA / sign-in endpoints. */
export interface SignInErrorLike {
  response?: { status?: number; data?: { detail?: string; message?: string } };
}

/**
 * The message to show for a failed MFA step: the rate-limit text on a 429,
 * else the server's `detail`, else `fallback`.
 *
 * @param err - The caught error
 * @param fallback - Text when the server gave nothing usable
 * @returns The message to display
 */
export function mfaErrorMessage(err: unknown, fallback: string): string {
  const response = (err as SignInErrorLike).response;
  if (response?.status === 429) return signInRateLimitMessage(response.data?.message);
  return response?.data?.detail || fallback;
}
